/* Adapted from https://github.com/jakearchibald/stacked-alpha-video */
const fragmentShaderSource = `
precision mediump float;

uniform vec3 u_color;
uniform bool u_with_overlay_color;

// our textures
uniform sampler2D u_frame;

// the texCoords passed in from the vertex shader.
varying vec2 v_texCoord;

void main() {
  // Calculate the coordinates for the color and alpha
  vec2 colorCoord = vec2(v_texCoord.x, v_texCoord.y * 0.5);
  vec2 alphaCoord = vec2(v_texCoord.x, 0.5 + v_texCoord.y * 0.5);

  vec4 color = texture2D(u_frame, colorCoord);
  float alpha = texture2D(u_frame, alphaCoord).r;

  gl_FragColor = u_with_overlay_color ? vec4(mix(color.rgb, u_color, alpha * (1.0 - alpha + 0.3)), alpha) : vec4(color.rgb, alpha);
}
`;

const vertexShaderSource = `
precision mediump float;
attribute vec2 a_position;
uniform mat3 u_matrix;
varying vec2 v_texCoord;

void main() {
  gl_Position = vec4(u_matrix * vec3(a_position, 1.0), 1.0);

  // because we're using a unit quad we can just use
  // the same data for our texcoords.
  v_texCoord = a_position;
}
`;

function loadShader(
  gl: WebGLRenderingContext | WebGL2RenderingContext,
  source: string,
  type: GLenum,
  isVertexShader: boolean
) {
  const shader = gl.createShader(type);
  if (!shader) throw Error('Unable to create shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const error = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw Error(
      `error when loading ${isVertexShader ? 'vertex shader' : 'fragment shader'}: ${error || 'unknown error'}`
    );
  }

  return shader;
}

function createProgram(
  gl: WebGLRenderingContext | WebGL2RenderingContext,
  shaders: WebGLShader[]
) {
  const program = gl.createProgram();
  if (!program) throw Error('Unable to create program');
  for (const shader of shaders) gl.attachShader(program, shader);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const error = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw Error(`error when linking program: ${error || 'unknown error'}`);
  }

  return program;
}

/**
 * Get a GL context for a canvas.
 * This only needs to be done once per canvas.
 */
export function setupGLContext(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  overlayColor?: [number, number, number]
) {
  const options: WebGLContextAttributes = {
    antialias: false,
    powerPreference: 'low-power',
    depth: false,
    premultipliedAlpha: true,
  };
  const context =
    canvas.getContext('webgl2', options) ?? canvas.getContext('webgl', options);

  if (!context) {
    if (window.WebGLRenderingContext) {
      throw Error('WebGL supported but could not create context');
    } else {
      throw Error('WebGL not supported');
    }
  }

  const frag = loadShader(
    context,
    fragmentShaderSource,
    context.FRAGMENT_SHADER,
    false
  );
  const vert = loadShader(
    context,
    vertexShaderSource,
    context.VERTEX_SHADER,
    true
  );
  const program = createProgram(context, [frag, vert]);
  context.useProgram(program);

  // look up where the vertex data needs to go.
  const positionLocation = context.getAttribLocation(program, 'a_position');

  // look up uniform locations
  const frameLoc = context.getUniformLocation(program, 'u_frame');
  context.uniform1i(frameLoc, 0); // texture unit 0
  const matrixLoc = context.getUniformLocation(program, 'u_matrix');
  const booleanLoc = context.getUniformLocation(
    program,
    'u_with_overlay_color'
  );
  const colorLoc = overlayColor
    ? context.getUniformLocation(program, 'u_color')
    : null;

  // provide texture coordinates for the rectangle.
  const positionBuffer = context.createBuffer();
  // prettier-ignore
  const rect = new Float32Array([
    0.0,  0.0,
    1.0,  0.0,
    0.0,  1.0,
    0.0,  1.0,
    1.0,  0.0,
    1.0,  1.0
  ]);

  context.bindBuffer(context.ARRAY_BUFFER, positionBuffer);
  context.bufferData(context.ARRAY_BUFFER, rect, context.STATIC_DRAW);
  context.enableVertexAttribArray(positionLocation);
  context.vertexAttribPointer(positionLocation, 2, context.FLOAT, false, 0, 0);
  context.uniformMatrix3fv(matrixLoc, false, [2, 0, 0, 0, -2, 0, -1, 1, 1]);

  if (overlayColor) {
    context.uniform3fv(colorLoc, overlayColor);
    context.uniform1i(booleanLoc, 1);
  } else {
    context.uniform1i(booleanLoc, 0);
  }

  const texture = context.createTexture();
  context.bindTexture(context.TEXTURE_2D, texture);
  // Set the parameters so we can render any size image.
  context.texParameteri(
    context.TEXTURE_2D,
    context.TEXTURE_WRAP_S,
    context.CLAMP_TO_EDGE
  );
  context.texParameteri(
    context.TEXTURE_2D,
    context.TEXTURE_WRAP_T,
    context.CLAMP_TO_EDGE
  );
  context.texParameteri(
    context.TEXTURE_2D,
    context.TEXTURE_MIN_FILTER,
    context.NEAREST
  );
  context.texParameteri(
    context.TEXTURE_2D,
    context.TEXTURE_MAG_FILTER,
    context.NEAREST
  );

  return context;
}

/**
 * Draw a stacked-alpha video frame to a GL context.
 */
export function drawVideo(
  context: WebGLRenderingContext | WebGL2RenderingContext,
  video: HTMLVideoElement
): void {
  const { canvas } = context;
  const width = video.videoWidth;
  const height = Math.floor(video.videoHeight / 2);

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
    context.viewport(0, 0, width, height);
  }

  context.texImage2D(
    context.TEXTURE_2D,
    0,
    context.RGBA,
    context.RGBA,
    context.UNSIGNED_BYTE,
    video
  );

  context.drawArrays(context.TRIANGLES, 0, 6);
}

// Safari's texImage2D throws `INVALID_VALUE: no video` when the element has no decoded frame yet (e.g. the source failed to load).
export const hasDecodableFrame = (video: HTMLVideoElement) => {
  return (
    video.readyState >= video.HAVE_CURRENT_DATA &&
    video.videoWidth > 0 &&
    video.videoHeight > 0
  );
};

export const isVideoPlaying = (video: HTMLVideoElement) => {
  return (
    video.currentTime > 0 &&
    !video.paused &&
    !video.ended &&
    video.readyState > 2
  );
};
